// MWA-390 — folding a client record that arrived over the WebSocket into the one on screen.
//
// The same helper serves every broadcast that carries a client (client_updated,
// workflow_advanced) and a quiet refetch after a reconnect, so a record changing
// elsewhere updates the open page without a reload.
//
// Two rules keep that from fighting the person using the page:
//  - A snapshot older than what is on screen is dropped. Broadcasts and the
//    caller's own HTTP response can arrive in either order.
//  - Fields that did not change keep their identity. Child components key effects
//    on pieces of the record (the comments list, for one); handing them a fresh
//    array that holds the same comments would re-run those effects and reset
//    whatever the person was in the middle of.

function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false
  if (Array.isArray(a) !== Array.isArray(b)) return false
  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)
  if (aKeys.length !== bKeys.length) return false
  return aKeys.every((key) =>
    sameValue((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key])
  )
}

export function mergeLiveClient<T extends { id: string; updatedAt?: string }>(
  current: T,
  incoming: Partial<T> | null | undefined
): T {
  if (!incoming) return current
  if (incoming.id !== undefined && incoming.id !== current.id) return current
  if (incoming.updatedAt && current.updatedAt && incoming.updatedAt < current.updatedAt) return current

  let changed = false
  const next: Record<string, unknown> = { ...current }
  for (const [key, value] of Object.entries(incoming)) {
    if (value === undefined) continue
    if (sameValue((current as Record<string, unknown>)[key], value)) continue
    next[key] = value
    changed = true
  }
  return changed ? (next as T) : current
}
